import { CurveMethod, WeibullFormulation } from '@Types';
import { isFlatSeries } from '@Components/deteriorationModel/utils/weibullFit';
import { CURVE_METHOD_COLORS, CHARTABLE_METHODS } from './constants';

export const getMethodDisplayName = (method: CurveMethod, t: (key: string) => string): string => {
	switch (method) {
		case CurveMethod.FAILURE_DEFINITION:
			return t('From Failure Definition');
		case CurveMethod.INDUSTRY_CURVE:
			return t('Industry Standard Curve');
		case CurveMethod.CUSTOM_CURVE:
			return t('Custom Curve');
		case CurveMethod.COPIED_CURVE:
			return t('Copy Curve from another Group');
		case CurveMethod.ONE_EXPECTED_LIFE:
			return t('One Expected Useful Life');
		default:
			return method;
	}
};

export const getMethodColor = (method: CurveMethod): string => {
	return CURVE_METHOD_COLORS[method] || '#757575';
};

export const convertCurvePointsToChartData = (curvePoints: CurvePoint[]): number[][] => {
	return curvePoints
		.filter(point => point.age !== null && point.survivalProbability !== null)
		.map(point => [point.age as number, point.survivalProbability as number]);
};

export const generateAvailableDataSeries = (
	t: (key: string) => string,
	customCurvePoints?: CurvePoint[],
	industryStandardCurvePoints?: CurvePoint[],
	failureDefinitionCurvePoints?: CurvePoint[],
	copiedCurvePoints?: CurvePoint[],
) => {
	const series = CHARTABLE_METHODS.map(method => {
		let data: number[][] = [];

		if (method === CurveMethod.CUSTOM_CURVE && customCurvePoints && customCurvePoints.length > 0) {
			data = convertCurvePointsToChartData(customCurvePoints);
		} else if (
			method === CurveMethod.INDUSTRY_CURVE &&
			industryStandardCurvePoints &&
			industryStandardCurvePoints.length > 0
		) {
			data = convertCurvePointsToChartData(industryStandardCurvePoints);
		} else if (
			method === CurveMethod.FAILURE_DEFINITION &&
			failureDefinitionCurvePoints &&
			failureDefinitionCurvePoints.length > 0
		) {
			data = convertCurvePointsToChartData(failureDefinitionCurvePoints);
		} else if (method === CurveMethod.COPIED_CURVE && copiedCurvePoints && copiedCurvePoints.length > 0) {
			data = convertCurvePointsToChartData(copiedCurvePoints);
		}

		return {
			id: method,
			name: getMethodDisplayName(method, t),
			data,
			color: getMethodColor(method),
		} as {
			id: string;
			name: string;
			data: number[][];
			color: string;
		};
	}).filter(series => series.data.length > 0);

	return series;
};

export interface CurvePoint {
	age: number | null;
	survivalProbability: number | null;
}

export type { WeibullParams } from '@Components/deteriorationModel/utils/weibullFit';

/** Survival-probability flavour of `isFlatSeries`, for the configuration-tab preview. */
export const isFlatCurve = (points: CurvePoint[]): boolean =>
	isFlatSeries(points.map(point => point.survivalProbability));

export interface CurveMethodData {
	selectedCurveMethod: CurveMethod;
	industryCurve: string | null;
	customCurve: CurvePoint[];
	oneExpectedLife: number | null;
	copiedCurve: string | null;
	weibullFormulation?: WeibullFormulation;
}

export interface CurveConfigData {
	method: CurveMethod;
	data: CurvePoint[] | number;
	reference: string;
}
